import { useState } from "react";
import { calendarService, taskService } from "@miplatform/api";
import {
  ATTEMPT_STATUSES,
  COLORS,
  HTTP_STATUSES,
  PLATFORM_MODULES,
} from "@miplatform/constants";
import { useComponentState, useRouter } from "@miplatform/hooks";
import { CalendarSetStatus } from "@miplatform/types";
import { getGUIDArray } from "@miplatform/utils";

const VIEW_ALL_BOARD = {
  name: "View All",
  id: "viewAll",
  label: "View All",
  value: "viewAll",
  default: true,
  showAllUsers: true,
};

const getCalendarCount = async (calendar) => {
  let count = null;
  if (calendar.calendarType === "HoldsList") {
    const response = await taskService.attempts.getAll({
      filters: {
        ...calendar.filter,
        attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
        isOrderFollowupDue: false,
        assignment: {
          order: {
            holdCalendarId: {
              type: "guid",
              value: calendar.id,
            },
            ...(calendar?.orderTemplateIds?.length > 0 && {
              orderTemplateId: {
                in: getGUIDArray(calendar.orderTemplateIds),
              },
            }),
          },
        },
      },
      sort: "modifiedOn desc",
      top: 0,
    });
    if (response.status === HTTP_STATUSES.OK) {
      count = response.headers.totalcount || 0;
    }
  }
  return count;
};

export const useScheduleViewModel = (calendarSetId) => {
  const [dispatchCalendarSet, setDispatchCalendarSet] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [selectedCalendarConfig, setSelectedCalendarConfig] = useState(null);
  const { navigate } = useRouter();
  const stateManager = useComponentState();

  const calendarTabs = (dispatchCalendarSet?.calendarConfigs || []).map(
    (calendar) => ({
      displayOrder: calendar.displayOrder,
      label: calendar.name,
      value: calendar.id,
      ...(calendar.count
        ? { chipLabel: calendar.count, chipColor: COLORS.RED }
        : {}),
    })
  );

  const modifyCalendarData = (calendar, calendarSet) => {
    calendar.trays = calendarSet.trays || [];
    calendar.orderTemplates = calendarSet.workTemplateBases || [];
    calendar.orderTemplateIds = (calendarSet.workTemplateBases || []).map(
      (template) => template.id
    );
    calendar.assignableUserIds = (calendar.assignableUsers || []).map(
      (user) => user.id
    );
    calendar.calendarSetId = calendarSet.id;
    calendar.serviceAreas = calendarSet.serviceAreas || [];
    calendar.displayBoards = [
      VIEW_ALL_BOARD,
      ...(calendar.boards?.length > 0
        ? calendar.boards.map((board) => ({
            ...board,
            label: board.name,
            value: board.id,
          }))
        : []),
    ];
    return calendar;
  };

  const handleActiveTabChange = (
    newValue,
    calendarSet = dispatchCalendarSet
  ) => {
    setActiveTab(newValue);
    setSelectedCalendarConfig(
      calendarSet.calendarConfigs.find((calendar) => calendar.id === newValue)
    );
  };

  const sortCalendarsByDisplayOrder = (calendars) => {
    return calendars.sort((a, b) => {
      const aOrder = a.displayOrder;
      const bOrder = b.displayOrder;
      if (aOrder === "" && bOrder === "") return 0;
      if (aOrder === "") return 1;
      if (bOrder === "") return -1;
      return aOrder - bOrder;
    });
  };

  const updateCalendarCounts = (calendarSet = dispatchCalendarSet) => {
    stateManager.run(async () => {
      const modifyCalendars = await Promise.all(
        calendarSet?.calendarConfigs.map(async (calendar) => {
          const count = await getCalendarCount(calendar);
          return {
            ...calendar,
            count,
          };
        })
      );
      setDispatchCalendarSet((prev) => ({
        ...prev,
        calendarConfigs: modifyCalendars,
      }));
    });
  };

  const getCalendarSet = () => {
    stateManager.run(async () => {
      const { data } = await calendarService.calendarSets.getMyCalendarSets({
        filters: { status: CalendarSetStatus.Active },
        sort: "displayOrder asc",
      });

      if (!data.length) return;

      const listItem =
        data.find((config) => config.id === calendarSetId) ||
        data.find((config) => config.module === PLATFORM_MODULES.MI_DISPATCH);

      if (!listItem) return;

      if (!calendarSetId) {
        navigate(`/dispatch/${listItem.id}`);
      }

      const detailsResponse = await calendarService.calendarSets.getById({
        id: listItem.id,
      });
      if (detailsResponse.status !== HTTP_STATUSES.OK) return;

      const miCalendarSet = {
        ...detailsResponse.data,
        trays: (detailsResponse.data.trays || []).filter(
          (tray) => tray.isActive
        ),
      };

      const attachedTrayCalendarMap = miCalendarSet.calendarConfigs.reduce(
        (acc, calendar) => {
          if (calendar.attachedTray) {
            if (!acc[calendar.attachedTray.id]) {
              acc[calendar.attachedTray.id] = [];
            }
            acc[calendar.attachedTray.id].push(calendar.id);
          }
          return acc;
        },
        {}
      );

      miCalendarSet.trays.forEach((tray) => {
        tray.attachedCalendarIds = attachedTrayCalendarMap[tray.id] || [];
      });

      miCalendarSet.calendarConfigs = miCalendarSet.calendarConfigs
        .map((calendar) => {
          return modifyCalendarData(calendar, miCalendarSet);
        })
        .sort((a, b) => a.displayOrder - b.displayOrder);

      sortCalendarsByDisplayOrder(miCalendarSet.calendarConfigs);
      setDispatchCalendarSet(miCalendarSet);
      updateCalendarCounts(miCalendarSet);
      if (miCalendarSet.calendarConfigs.length > 0) {
        handleActiveTabChange(
          miCalendarSet.calendarConfigs?.[0].id,
          miCalendarSet
        );
      }
    });
  };

  return {
    loading: stateManager.isBusy(),
    getCalendarSet,
    selectedCalendarConfig,
    calendarTabs,
    handleActiveTabChange,
    updateCalendarCounts,
    activeTab,
    calendarSet: dispatchCalendarSet,
  };
};
